// ------------------------------------------------------------------
// use-dropdown-floating-v2 — shared placement and sizing for V2 popups.
//
// Two different ARIA widgets hang a portaled panel off a trigger: the
// combobox/listbox behind ComboboxV2 and SelectMenuV2, and the Headless UI menu
// behind IconButtonDropdownMenuV2. Their roles, keyboard models and
// value-vs-action semantics are deliberately different and stay in their own
// components. Their geometry is not: both have to survive an ancestor that
// clips, a viewport edge that is too close, and a panel that wants to know how
// wide its trigger is. That part lives here, once, so the two cannot drift into
// different viewport margins or different height behaviour again.
// ------------------------------------------------------------------

import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
  type OffsetOptions,
  type UseFloatingOptions,
} from "@floating-ui/react";
import { useState } from "react";

/**
 * Gap kept between the menu and the viewport edge.
 *
 * `flip`, `shift` and `size` all read the same number, so the side chosen to
 * avoid an edge, the nudge along that edge, and the height measured against it
 * agree on where the edge is.
 */
export const MENU_VIEWPORT_MARGIN = 12;

/** Gap between the trigger and the menu hanging off it. */
export const MENU_TRIGGER_GAP = 4;

export type UseDropdownFloatingV2Options = Omit<
  UseFloatingOptions,
  "middleware" | "whileElementsMounted"
> & {
  open: boolean;
  /**
   * Handed straight to floating-ui's `offset`, so a caller can nudge along
   * either axis: `crossAxis` lines the menu's item text up with a trigger that
   * has no padding of its own.
   */
  offset?: OffsetOptions;
  /**
   * Ceiling on the menu's height. `menuMaxHeight` is the smaller of this and the
   * room the trigger actually has, so a long menu near the bottom of the
   * viewport scrolls inside itself instead of spilling past the edge. Omit it for
   * a menu that should grow into whatever room it has.
   */
  maxHeight?: number;
};

export type UseDropdownFloatingV2Result = Pick<
  ReturnType<typeof useFloating>,
  "refs" | "floatingStyles"
> & {
  /**
   * Height cap for the menu's scroll container, or undefined when nothing caps
   * it. Apply it alongside `overflow-y: auto` — a cap without a scroll container
   * clips the rows instead of letting the user reach them.
   */
  menuMaxHeight: number | undefined;
  /**
   * Horizontal room left inside the clipping viewport after placement and
   * shifting. Content-sized menus use this as their ceiling; explicit widths
   * remain the caller's responsibility.
   */
  menuMaxWidth: number | undefined;
  /** Undefined until the first measurement lands. */
  triggerWidth: number | undefined;
};

export const useDropdownFloatingV2 = ({
  open,
  offset: offsetOptions = MENU_TRIGGER_GAP,
  maxHeight,
  ...floatingOptions
}: UseDropdownFloatingV2Options): UseDropdownFloatingV2Result => {
  // Room the trigger has on the side the menu ended up on, measured against the
  // live viewport. Undefined until the middleware has run once.
  const [availableHeight, setAvailableHeight] = useState<number | undefined>(undefined);
  const [availableWidth, setAvailableWidth] = useState<number | undefined>(undefined);
  // Read off the rects floating-ui has already measured for placement, so
  // opening the menu or typing into it never issues an extra
  // getBoundingClientRect. Stale while closed is fine: a caller that unmounts
  // its menu re-measures before the next one is visible.
  const [triggerWidth, setTriggerWidth] = useState<number | undefined>(undefined);

  const { refs, floatingStyles } = useFloating({
    open,
    middleware: [
      offset(offsetOptions),
      // `flip` before `shift`: prefer the opposite side of the trigger when this
      // one has no room, and only slide along the edge once neither side fits.
      flip({ padding: MENU_VIEWPORT_MARGIN }),
      shift({ padding: MENU_VIEWPORT_MARGIN }),
      // Last in the stack, so it measures the placement flip and shift settled
      // on rather than the requested one.
      size({
        padding: MENU_VIEWPORT_MARGIN,
        apply({ availableHeight: available, availableWidth: horizontalRoom, rects }) {
          setAvailableHeight(Math.max(0, available));
          setAvailableWidth(Math.max(0, horizontalRoom));
          setTriggerWidth(rects.reference.width);
        },
      }),
    ],
    // Gated on `open` because a caller may keep its positioned wrapper mounted
    // while the menu is closed; an unconditional autoUpdate would leave scroll
    // and resize listeners attached for every dropdown on the page.
    whileElementsMounted: open ? autoUpdate : undefined,
    ...floatingOptions,
  });

  return {
    refs,
    floatingStyles,
    menuMaxHeight:
      availableHeight == null
        ? maxHeight
        : maxHeight == null
          ? availableHeight
          : Math.min(maxHeight, availableHeight),
    menuMaxWidth: availableWidth,
    triggerWidth,
  };
};
